/**
 * @file appSettings.ts
 *
 * Runtime registry of per-environment app settings. Populated at module-load
 * time by plugin registrations so that no core module needs hardcoded knowledge
 * of plugin-specific behaviour (e.g. GrabCAD hiding the transfer "open in new
 * tab" option). Settings are keyed by env string because plugins self-register
 * unconditionally at import time — the env decides which settings apply.
 *
 * Example — in grabcadPlugin.ts:
 *   registerAppSettings('grabcad', { allowTransferNewTab: false });
 */

/** Per-environment behavioural toggles a plugin can override. */
export interface AppSettings {
  /** Whether the transfer modal offers an "open in new tab" option. */
  allowTransferNewTab: boolean;
}

const DEFAULT_SETTINGS: AppSettings = {
  allowTransferNewTab: true,
};

const _settingsByEnv = new Map<string, Partial<AppSettings>>();

/**
 * Register (or merge) settings overrides for an env. Called by plugin modules on
 * load. Repeated calls for the same env merge shallowly, last value wins.
 */
export function registerAppSettings(env: string, settings: Partial<AppSettings>): void {
  _settingsByEnv.set(env, { ..._settingsByEnv.get(env), ...settings });
}

/**
 * Return the effective settings for an env: registered overrides merged over the
 * defaults. An unknown or empty env yields the defaults.
 */
export function getAppSettings(env: string | null | undefined): AppSettings {
  return { ...DEFAULT_SETTINGS, ...(env ? _settingsByEnv.get(env) : undefined) };
}
